import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Chip,
    Switch,
    FormControlLabel,
    Divider,
    Grid,
    Paper,
    TextField,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Checkbox
} from '@mui/material';
import {
    ExpandMore,
    AutoAwesome,
    Translate,
    Update,
    Add,
    Check,
    Close
} from '@mui/icons-material';

const AITranslationDialog = ({ 
    open, 
    onClose, 
    aiResponse, 
    onApplyChanges,
    currentFullTranslation,
    onResendRequest,
    isLoading = false
}) => {
    const [selectedWords, setSelectedWords] = useState(new Set());
    const [selectedPhrases, setSelectedPhrases] = useState(new Set());
    const [selectedNewPhrases, setSelectedNewPhrases] = useState(new Set());
    const [fullTranslation, setFullTranslation] = useState(aiResponse?.full_translation || '');

    // Update fullTranslation when aiResponse changes (for resend requests)
    useEffect(() => {
        if (aiResponse?.full_translation) {
            setFullTranslation(aiResponse.full_translation);
        }
    }, [aiResponse]);

    // Reset selections when dialog opens with new response
    useEffect(() => {
        if (open && aiResponse) {
            setSelectedWords(new Set());
            setSelectedPhrases(new Set());
            setSelectedNewPhrases(new Set());
        }
    }, [open, aiResponse]);

    if (!aiResponse) return null;

    const handleWordToggle = (wordId) => {
        const newSelected = new Set(selectedWords);
        if (newSelected.has(wordId)) {
            newSelected.delete(wordId);
        } else {
            newSelected.add(wordId);
        }
        setSelectedWords(newSelected);
    };

    const handlePhraseToggle = (phraseId) => {
        const newSelected = new Set(selectedPhrases);
        if (newSelected.has(phraseId)) {
            newSelected.delete(phraseId);
        } else {
            newSelected.add(phraseId);
        }
        setSelectedPhrases(newSelected);
    };

    const handleNewPhraseToggle = (index) => {
        const newSelected = new Set(selectedNewPhrases);
        if (newSelected.has(index)) {
            newSelected.delete(index);
        } else {
            newSelected.add(index);
        }
        setSelectedNewPhrases(newSelected);
    };

    const handleSelectAllWords = () => {
        const updatedWords = aiResponse.updated_words?.filter(word => word.updated) || [];
        if (selectedWords.size === updatedWords.length) {
            setSelectedWords(new Set());
        } else {
            setSelectedWords(new Set(updatedWords.map(word => word.id)));
        }
    };

    const handleSelectAllPhrases = () => {
        const updatedPhrases = aiResponse.updated_phrases?.filter(phrase => phrase.updated) || [];
        if (selectedPhrases.size === updatedPhrases.length) {
            setSelectedPhrases(new Set());
        } else {
            setSelectedPhrases(new Set(updatedPhrases.map(phrase => phrase.id)));
        }
    };

    const handleSelectAllNewPhrases = () => {
        const newPhrases = aiResponse.new_phrases || [];
        if (selectedNewPhrases.size === newPhrases.length) {
            setSelectedNewPhrases(new Set());
        } else {
            setSelectedNewPhrases(new Set(newPhrases.map((_, index) => index)));
        }
    };

    const handleApply = () => {
        const changes = {
            words: Array.from(selectedWords),
            phrases: Array.from(selectedPhrases),
            newPhrases: Array.from(selectedNewPhrases),
            fullTranslation: fullTranslation !== currentFullTranslation ? fullTranslation : null
        };
        onApplyChanges(changes, aiResponse);
        onClose();
    };

    const updatedWords = aiResponse.updated_words?.filter(word => word.updated) || [];
    const updatedPhrases = aiResponse.updated_phrases?.filter(phrase => phrase.updated) || [];
    const newPhrases = aiResponse.new_phrases || [];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Box display="flex" alignItems="center">
                    <AutoAwesome sx={{ mr: 1, color: 'primary.main' }} />
                    AI Translation Suggestions
                </Box>
            </DialogTitle>
            
            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Review the AI suggestions below and select which ones to apply to your translation.
                    </Alert>

                    {/* Full Translation */}
                    <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="h6">
                                <Translate sx={{ mr: 1, verticalAlign: 'bottom' }} />
                                Full Translation
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                value={fullTranslation}
                                onChange={(e) => setFullTranslation(e.target.value)}
                                variant="outlined"
                                label="AI Generated Translation"
                                helperText="Edit the translation as needed"
                            />
                        </AccordionDetails>
                    </Accordion>

                    {/* Updated Words */}
                    {updatedWords.length > 0 && (
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="h6">
                                    <Update sx={{ mr: 1, verticalAlign: 'bottom' }} />
                                    Updated Words ({updatedWords.length})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{ mb: 2 }}>
                                    <Button 
                                        onClick={handleSelectAllWords}
                                        size="small"
                                        startIcon={selectedWords.size === updatedWords.length ? <Close /> : <Check />}
                                    >
                                        {selectedWords.size === updatedWords.length ? 'Deselect All' : 'Select All'}
                                    </Button>
                                </Box>
                                <List>
                                    {updatedWords.map((word) => (
                                        <ListItem key={word.id} dense>
                                            <ListItemIcon>
                                                <Checkbox
                                                    checked={selectedWords.has(word.id)}
                                                    onChange={() => handleWordToggle(word.id)}
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Box>
                                                        <Typography component="span" sx={{ fontWeight: 'bold' }}>
                                                            {word.word}
                                                        </Typography>
                                                        <Chip 
                                                            label={word.pos} 
                                                            size="small" 
                                                            sx={{ ml: 1 }}
                                                        />
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary">
                                                            AI suggestion: <strong>{word.translation}</strong>
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {/* Updated Phrases */}
                    {updatedPhrases.length > 0 && (
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="h6">
                                    <Update sx={{ mr: 1, verticalAlign: 'bottom' }} />
                                    Updated Phrases ({updatedPhrases.length})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{ mb: 2 }}>
                                    <Button 
                                        onClick={handleSelectAllPhrases}
                                        size="small"
                                        startIcon={selectedPhrases.size === updatedPhrases.length ? <Close /> : <Check />}
                                    >
                                        {selectedPhrases.size === updatedPhrases.length ? 'Deselect All' : 'Select All'}
                                    </Button>
                                </Box>
                                <List>
                                    {updatedPhrases.map((phrase) => (
                                        <ListItem key={phrase.id} dense>
                                            <ListItemIcon>
                                                <Checkbox
                                                    checked={selectedPhrases.has(phrase.id)}
                                                    onChange={() => handlePhraseToggle(phrase.id)}
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Typography sx={{ fontWeight: 'bold' }}>
                                                        {phrase.phrase}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Typography variant="body2" color="text.secondary">
                                                        AI suggestion: <strong>{phrase.translation}</strong>
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {/* New Phrases */}
                    {newPhrases.length > 0 && (
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography variant="h6">
                                    <Add sx={{ mr: 1, verticalAlign: 'bottom' }} />
                                    New Phrases ({newPhrases.length})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{ mb: 2 }}>
                                    <Button 
                                        onClick={handleSelectAllNewPhrases}
                                        size="small"
                                        startIcon={selectedNewPhrases.size === newPhrases.length ? <Close /> : <Check />}
                                    >
                                        {selectedNewPhrases.size === newPhrases.length ? 'Deselect All' : 'Select All'}
                                    </Button>
                                </Box>
                                <List>
                                    {newPhrases.map((phrase, index) => (
                                        <ListItem key={index} dense>
                                            <ListItemIcon>
                                                <Checkbox
                                                    checked={selectedNewPhrases.has(index)}
                                                    onChange={() => handleNewPhraseToggle(index)}
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Typography sx={{ fontWeight: 'bold' }}>
                                                        {phrase.phrase}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Typography variant="body2" color="text.secondary">
                                                        Translation: <strong>{phrase.translation}</strong>
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {/* Summary */}
                    <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Summary of Changes
                        </Typography>
                        <Typography variant="body2">
                            • {selectedWords.size} words will be updated
                        </Typography>
                        <Typography variant="body2">
                            • {selectedPhrases.size} phrases will be updated
                        </Typography>
                        <Typography variant="body2">
                            • {selectedNewPhrases.size} new phrases will be added
                        </Typography>
                        {fullTranslation !== currentFullTranslation && (
                            <Typography variant="body2">
                                • Full translation will be updated
                            </Typography>
                        )}
                    </Paper>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>
                    Cancel
                </Button>
                {onResendRequest && (
                    <Button 
                        onClick={onResendRequest}
                        disabled={isLoading}
                        color="secondary"
                        startIcon={isLoading ? <span>⏳</span> : <span>🔄</span>}
                    >
                        {isLoading ? 'Resending...' : 'Resend Request'}
                    </Button>
                )}
                <Button 
                    onClick={handleApply} 
                    variant="contained" 
                    color="primary"
                    disabled={selectedWords.size === 0 && selectedPhrases.size === 0 && selectedNewPhrases.size === 0 && fullTranslation === currentFullTranslation}
                >
                    Apply Selected Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AITranslationDialog; 